import React, { useEffect, useRef, useState } from "react";

export type ShakaPlayerProps = {
  src: string; // DASH .mpd
  autoPlay?: boolean;
  loop?: boolean;
  controls?: boolean;
  showSubtitles?: boolean;
  style?: React.CSSProperties;
  shakaOptions?: {
    config?: Record<string, any>;
    drm?: Record<string, string>;
  };
};

const ShakaPlayer: React.FC<ShakaPlayerProps> = ({
  src,
  autoPlay = true,
  loop = false,
  controls = false,
  showSubtitles = false,
  style,
  shakaOptions,
}) => {
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const shakaPlayerRef = useRef<any>(null);

  useEffect(() => {
    let mounted = true;
    let onEnded: (() => void) | null = null;
    let onError: ((e: any) => void) | null = null;

    const setupShaka = async () => {
      if (!videoEl) return;
      try {
        // @ts-expect-error: dynamic import types
        const mod = await import("shaka-player");
        const shaka: any = mod.default ?? mod;
        shaka.polyfill.installAll();
        if (!mounted) return;

        if (!shaka.Player.isBrowserSupported()) {
          console.warn("Shaka Player: browser not supported");
          return;
        }

        const player = new shaka.Player(); // v5+ style
        await player.attach(videoEl);
        shakaPlayerRef.current = player;

        if (shakaOptions?.config) player.configure(shakaOptions.config);
        if (shakaOptions?.drm)
          player.configure({ drm: { servers: shakaOptions.drm } });

        onEnded = async () => {
          if (!loop || !shakaPlayerRef.current || !videoEl) return;
          try {
            const start = shakaPlayerRef.current.seekRange().start;
            await shakaPlayerRef.current.seek(start);
            await videoEl.play();
          } catch {
            console.warn("Failed to loop video");
          }
        };

        onError = (e: any) => {
          console.error("Shaka Player error:", e);
          if (e instanceof shaka.util.Error) {
            console.error("Shaka Player error code:", e.code);
          }
        };

        videoEl.addEventListener("ended", onEnded);
        videoEl.addEventListener("error", onError);

        await player.load(src);

        if (showSubtitles) {
          const textTracks = player.getTextTracks();
          if (textTracks.length > 0) {
            if (!textTracks.some((track: any) => track.active)) {
              player.selectTextTrack(textTracks[0]);
            }
            await player.setTextTrackVisibility(true);
          }
        }

        if (autoPlay) {
          try {
            videoEl.muted = true;
            await videoEl.play();
          } catch (e) {
            console.warn("Failed to play video:", e);
          }
        }
      } catch (err) {
        console.error("Failed to load DASH with Shaka:", err);
      }
    };

    setupShaka();

    return () => {
      mounted = false;
      if (videoEl) {
        if (onEnded) videoEl.removeEventListener("ended", onEnded);
        if (onError) videoEl.removeEventListener("error", onError);
      }
      if (shakaPlayerRef.current) {
        shakaPlayerRef.current.destroy().catch(() => {});
        shakaPlayerRef.current = null;
      }
    };
  }, [videoEl, src, autoPlay, loop, showSubtitles, shakaOptions]);

  return (
    <video
      key={src}
      ref={setVideoEl}
      controls={controls}
      playsInline
      style={style}
    />
  );
};

export default ShakaPlayer;
